import {
  Barcode,
  BarcodeCapture,
  BarcodeCaptureOverlay,
  BarcodeCaptureSession,
  BarcodeCaptureSettings,
  barcodeCaptureLoader,
} from "@scandit/web-datacapture-barcode";
import {
  Camera,
  CameraSwitchControl,
  DataCaptureContext,
  DataCaptureView,
  FrameSourceState,
  LaserlineViewfinder,
  Viewfinder,
} from "@scandit/web-datacapture-core";
import { scanditLicense } from "env";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { getSymbologies } from "./Symbologies";

export type BarcodeScannerRef = {
  onTurnOffCamera: () => void;
};

interface BarcodeScannerProps {
  submitValue: (value: string) => void;
}

export const BarcodeScanner = forwardRef<BarcodeScannerRef, BarcodeScannerProps>((props, ref) => {
  const { submitValue } = props;
  const [turnOffCamera, setTurnOffCamera] = useState<() => void>(() => () => {});
  const [context, setContext] = useState<DataCaptureContext | null>(null);

  const pageElements = {
    captureHost: document.getElementById("data-capture-view") as HTMLElement,
  };

  useImperativeHandle(ref, () => ({
    onTurnOffCamera() {
      turnOffCamera();
    },
  }));

  useEffect(() => {
    if (context) return;
    DataCaptureContext.forLicenseKey(scanditLicense()!, {
      moduleLoaders: [barcodeCaptureLoader({ highEndBlurryRecognition: false })],
      libraryLocation:
        "https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.2.0/sdc-lib/",
    }).then((context) => {
      setContext(context);
    });
  });

  const takePhoto = useCallback(async () => {
    if (!context) return;
    // Try to use the world-facing (back) camera and set it as the frame source of the context. The camera is off by
    // default and must be turned on to start streaming frames to the data capture context for recognition.
    const camera = Camera.pickBestGuess();
    await camera.applySettings(BarcodeCapture.recommendedCameraSettings);
    await camera.setMirrorImageEnabled(false);
    await context.setFrameSource(camera);

    // The barcode capturing process is configured through barcode capture settings,
    // they are then applied to the barcode capture instance that manages barcode recognition.
    const settings: BarcodeCaptureSettings = new BarcodeCaptureSettings();
    // The settings instance initially has all types of barcodes (symbologies) disabled. For the purpose of this
    // sample we enable a very generous set of symbologies. In your own app ensure that you only enable the
    // symbologies that your app requires as every additional enabled symbology has an impact on processing times.
    settings.enableSymbologies(getSymbologies());
    // Filter out duplicate barcodes for 1 second.
    settings.codeDuplicateFilter = -1;

    // Create a new barcode capture mode with the settings from above.
    const barcodeCapture = await BarcodeCapture.forContext(context, settings);

    // Disable the barcode capture mode until the camera is accessed.
    barcodeCapture.setEnabled(false);
    // Register a listener to get informed whenever a new barcode got recognized.
    barcodeCapture.addListener({
      didScan: (barcodeCapture: BarcodeCapture, session: BarcodeCaptureSession) => {
        turnOffCamera();
        const barcode: Barcode | null = session.newlyRecognizedBarcode;
        if (barcode && barcode.data) submitValue(barcode.data);
      },
    });

    // To visualize the ongoing barcode capturing process on screen, set up a data capture view that renders the
    // camera preview. The view must be connected to the data capture context.
    const view = await DataCaptureView.forContext(context);

    // Connect the data capture view to the HTML element.
    view.connectToElement(pageElements.captureHost);

    // Add a control to be able to switch cameras.
    view.addControl(new CameraSwitchControl());

    // Add a barcode capture overlay to the data capture view to render the location of captured barcodes on top of
    // the video preview. This is optional, but recommended for better visual feedback.
    const barcodeCaptureOverlay: BarcodeCaptureOverlay =
      await BarcodeCaptureOverlay.withBarcodeCaptureForView(barcodeCapture, view);
    const viewfinder: Viewfinder = new LaserlineViewfinder();
    barcodeCaptureOverlay.setViewfinder(viewfinder);

    // Switch the camera on to start streaming frames.
    let turnOffCamera = () => {
      barcodeCapture.setEnabled(false);
      camera.switchToDesiredState(FrameSourceState.Off);
      context.dispose();
      setContext(null);
      setTurnOffCamera(() => () => {});
    };
    setTurnOffCamera(() => turnOffCamera);

    camera.switchToDesiredState(FrameSourceState.On).then(() => {
      barcodeCapture.setEnabled(true);
    });
  }, [context, pageElements.captureHost, submitValue]);

  useEffect(() => {
    takePhoto();
  }, [pageElements.captureHost, submitValue, setTurnOffCamera, takePhoto, context]);

  return (
    <div className="wrapper">
      <div className="top">
        <div
          style={{
            display: "grid",
            position: "fixed",
            top: "0",
            left: "0",
            width: "100vw",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            id="data-capture-view"
            style={{
              position: "relative",
              height: "100%",
              width: "100%",
            }}
          />
        </div>
      </div>
    </div>
  );
});
